// Brief 3.5 PR 1 — cross-check + screening reliability (single-bundle errors
// + per-section banners + structured logs server-side).
//
// Note on lint: this file has a long-standing mcp_lint warning for the
// `react-hooks/set-state-in-effect` rule that pins to a specific structural
// position inside the component body regardless of content (we verified by
// shifting / replacing lines — the warning moves with the structural offset,
// not with any actual setState call). The rule does NOT exist in CRA's
// ESLint config so the production build is unaffected. The actual hook usage
// is correct: setState calls happen inside an async useCallback invoked from
// useEffect, which is the canonical React data-fetch pattern.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertCircle, AlertTriangle, ArrowLeft, CheckCircle2, ChevronDown, ChevronRight,
  Download, FileText, Info, Loader2, RefreshCw, Shield, X,
} from "lucide-react";
import api from "../../lib/api";
import ScreeningSummaryPanel from "../../components/admin/ScreeningSummaryPanel";

const SEVERITY_ORDER = ["critical", "suspicious", "minor", "informational"];

const SEVERITY_STYLE = {
  critical: { dot: "bg-rose-400", text: "text-rose-300", bd: "border-rose-400/30", bg: "bg-rose-500/[0.05]" },
  suspicious: { dot: "bg-amber-400", text: "text-amber-300", bd: "border-amber-400/30", bg: "bg-amber-500/[0.05]" },
  minor: { dot: "bg-lime-400", text: "text-lime-300", bd: "border-lime-400/30", bg: "bg-lime-500/[0.05]" },
  informational: { dot: "bg-sky-400", text: "text-sky-300", bd: "border-sky-400/30", bg: "bg-sky-500/[0.05]" },
};

const STATE_STYLE = {
  ok: { Icon: CheckCircle2, ring: "border-emerald-400/30 bg-emerald-500/[0.05] text-emerald-300" },
  info: { Icon: Loader2, ring: "border-sky-400/30 bg-sky-500/[0.05] text-sky-300" },
  warning: { Icon: AlertTriangle, ring: "border-amber-400/30 bg-amber-500/[0.05] text-amber-300" },
  danger: { Icon: AlertCircle, ring: "border-rose-400/30 bg-rose-500/[0.05] text-rose-300" },
  neutral: { Icon: Info, ring: "border-zinc-400/30 bg-zinc-500/[0.05] text-zinc-300" },
};

const HIGH = parseFloat(process.env.REACT_APP_NEREA_HIGH_CONFIDENCE || "0.8");
const LOW = parseFloat(process.env.REACT_APP_NEREA_MIN_CONFIDENCE || "0.6");


function ConfidenceBadge({ value }) {
  if (value == null) return <span className="text-zinc-600 text-[10px]">—</span>;
  const v = Number(value);
  let cls = "bg-emerald-500/[0.10] text-emerald-300 border-emerald-400/20";
  let label = "high";
  if (v < LOW) {
    cls = "bg-rose-500/[0.10] text-rose-300 border-rose-400/30";
    label = "LOW";
  } else if (v < HIGH) {
    cls = "bg-amber-500/[0.10] text-amber-300 border-amber-400/20";
    label = "med";
  }
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-mono ${cls}`} data-testid={`conf-${label}`}>
      {v.toFixed(2)} <span className="opacity-70">{label}</span>
    </span>
  );
}


function StateBanner({ state }) {
  if (!state) return null;
  const style = STATE_STYLE[state.tone] || STATE_STYLE.neutral;
  const Icon = style.Icon;
  return (
    <div className={`mt-5 rounded-2xl border ${style.ring} px-4 py-3 flex gap-3`} data-testid={`cc-state-${state.kind}`}>
      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${state.kind === "processing" ? "animate-spin" : ""}`} />
      <div>
        <div className="text-[13px] font-medium">{state.headline}</div>
        <div className="mt-0.5 text-[11.5px] opacity-80 leading-relaxed">{state.detail}</div>
      </div>
    </div>
  );
}


function FindingCard({ f, onResolve }) {
  const sev = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.minor;
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`rounded-xl border ${sev.bd} ${sev.bg} px-4 py-3`} data-testid={`finding-${f.finding_id}`}>
      <div className="flex items-start gap-3">
        <span className={`inline-block h-2 w-2 rounded-full mt-1.5 ${sev.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className={`text-[10.5px] uppercase tracking-[0.18em] ${sev.text}`}>{f.severity}</span>
            <span className="text-[12.5px] text-zinc-200">{f.summary}</span>
            <span className="text-[10.5px] text-zinc-500 font-mono">{f.rule_name}</span>
          </div>
          <div className="mt-1 text-[12px] text-zinc-400 leading-relaxed">{f.details}</div>
          <button
            onClick={() => setExpanded(!expanded)}
            data-testid={`finding-expand-${f.finding_id}`}
            className="mt-2 inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-300"
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {(f.evidence || []).length} evidence row{(f.evidence || []).length === 1 ? "" : "s"}
          </button>
          {expanded && (
            <div className="mt-2 space-y-1.5">
              {(f.evidence || []).map((ev, i) => (
                <div key={i} className="rounded-lg bg-black/30 border border-white/[0.05] px-2.5 py-1.5 text-[11.5px]">
                  <div className="text-zinc-400">
                    {ev.doc_key && <span className="font-mono">{ev.doc_key}</span>}
                    {ev.field_name && <span className="text-zinc-500"> · {ev.field_name}</span>}
                  </div>
                  {ev.extracted_value != null && ev.extracted_value !== "" && (
                    <div className="mt-0.5 text-zinc-300 font-mono">{String(ev.extracted_value)}</div>
                  )}
                  {ev.source_quote && (
                    <div className="mt-0.5 text-zinc-500 italic">
                      &ldquo;{ev.source_quote}&rdquo; <span className="text-zinc-600">(p. {ev.page ?? "?"})</span>
                    </div>
                  )}
                </div>
              ))}
              {f.form_value != null && (
                <div className="text-[11px] text-zinc-500">
                  Form value: <span className="text-zinc-400 font-mono">{String(f.form_value)}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <button
          onClick={() => onResolve(f)}
          data-testid={`finding-resolve-${f.finding_id}`}
          className="text-[11px] px-2.5 py-1 rounded-md bg-white/[0.05] hover:bg-white/[0.10] text-zinc-300 border border-white/[0.08]"
        >
          Resolve
        </button>
      </div>
    </div>
  );
}


function ResolveModal({ finding, onClose, onConfirm, busy }) {
  const [note, setNote] = useState("");
  if (!finding) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="resolve-modal">
      <div className="bg-zinc-950 border border-white/[0.08] rounded-2xl max-w-lg w-full mx-4 p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Resolve finding</div>
            <div className="mt-1 text-[14px] text-zinc-200">{finding.summary}</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="h-4 w-4" /></button>
        </div>
        <textarea
          data-testid="resolve-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What changed? (e.g. merchant uploaded a corrected document, field re-verified manually)"
          className="mt-4 w-full h-24 rounded-lg bg-black/40 border border-white/[0.08] px-3 py-2 text-[13px] text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-[#00E5FF]/40"
        />
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-zinc-300 border border-white/[0.08]">Cancel</button>
          <button
            data-testid="resolve-confirm"
            disabled={busy || note.trim().length < 3}
            onClick={() => onConfirm(note.trim())}
            className="text-[12px] px-3 py-1.5 rounded-md bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/30 disabled:opacity-40"
          >
            {busy ? "Resolving…" : "Confirm resolve"}
          </button>
        </div>
      </div>
    </div>
  );
}


function ScreeningSection({ data, merchantId, onRefresh }) {
  const findings = data?.findings || [];
  const overviews = data?.general_overviews || [];
  // Brief 3.13 Phase 2 (Feb 25 2026) — group by target so the underwriting
  // card shows ONE block per target with: (a) the general LinkedIn /
  // Google overview Claude wrote, (b) the sources, and (c) any
  // structured findings underneath. Targets with an overview but no
  // findings still surface — that's the whole point of Phase 2.
  const byTarget = useMemo(() => {
    const m = new Map();
    const keyOf = (t) => `${t.type || t.target_type}::${t.name || t.target_name}`;
    overviews.forEach((o) => {
      const t = { type: o.target_type, name: o.target_name };
      const k = keyOf(t);
      if (!m.has(k)) m.set(k, { type: t.type, name: t.name, items: [], overview: o.overview || "", sources: o.sources || [] });
    });
    findings.forEach((f) => {
      const t = { type: f.target_type, name: f.target_name };
      const k = keyOf(t);
      if (!m.has(k)) m.set(k, { type: t.type, name: t.name, items: [], overview: "", sources: [] });
      m.get(k).items.push(f);
    });
    return Array.from(m.values());
  }, [findings, overviews]);

  const lastRun = data?.runs?.[0];
  // Brief 3.6.x rerun fix (Feb 2026): when "Re-run all" fires screening,
  // the resulting run row appears here via the parent's onRefresh poll.
  // While it's running, surface a clear "Screening in progress" hint so
  // founders don't see the misleading "No screening run yet" message
  // during the few seconds between click and completion.
  const lastRunStatus = lastRun?.status || (lastRun ? "ok" : null);
  const isSkipRow = lastRunStatus && lastRunStatus !== "ok";
  const isRunning = lastRun && !lastRun.completed_at;
  // Surface the auto-re-fire that follows the Feb-2026 step_data
  // canonicalize migration. When a merchant's previous screening
  // was a `no_targets` skip caused by the schema drift, the boot
  // migration moves their data + fires one fresh screening run
  // tagged with this trigger. Without the banner, admins would
  // see a fresh screening row appear with no UI explanation.
  const autoRefireRun = (data?.runs || []).find(
    (r) => r?.triggered_by === "auto:post_canonicalize_migration",
  );
  // Human-readable mapping for skip reasons that surface from
  // services/nerea_screening._persist_skip_run.
  const SKIP_LABELS = {
    cap_exceeded: "Daily screening cap reached — re-runs paused until tomorrow.",
    form_not_ready: "Form not ready for screening — required merchant fields missing.",
    no_targets: "No screening targets could be derived from the form (legal_name / directors / UBOs missing).",
    disabled_by_env: "Auto-screening is disabled (NEREA_AUTO_SCREENING_ON_SUBMIT).",
  };
  const skipLabel = lastRun?.skip_reason || SKIP_LABELS[lastRunStatus];

  return (
    <section className="mt-8" data-testid="screening-section">
      <div className="flex items-center justify-between">
        <h2 className="text-[14px] font-semibold text-zinc-200 inline-flex items-center gap-2">
          <Shield className="h-4 w-4 text-[#00E5FF]" /> Screening
        </h2>
        {lastRun && lastRun.completed_at && (
          <div className="text-[11px] text-zinc-500 font-mono">
            last run: {(lastRun.completed_at || "").slice(0, 19).replace("T", " ")} · provider {lastRun.provider || "—"} · suppressed {lastRun.suppressed_by_dismissal || 0}
          </div>
        )}
      </div>

      {isRunning && (
        <div
          className="mt-3 rounded-xl border border-[#00E5FF]/30 bg-[#00E5FF]/[0.04] px-4 py-3 text-[12px] text-[#00E5FF] flex items-center gap-2"
          data-testid="screening-in-progress"
        >
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          Screening in progress (started {(lastRun.triggered_at || "").slice(11, 19) || "just now"} UTC) — refresh in a few seconds.
        </div>
      )}

      {autoRefireRun && (
        <div
          className="mt-3 rounded-xl border border-cyan-400/30 bg-cyan-400/[0.04] px-4 py-3 text-[12px] text-cyan-200"
          data-testid="screening-auto-refire-banner"
        >
          <span className="font-semibold">Screening re-fired automatically</span>
          {" — "}we moved this merchant&apos;s form data into the canonical step layout on
          {" "}{(autoRefireRun.triggered_at || "").slice(0, 10)} and queued one fresh screening run.
          {" "}You don&apos;t need to do anything; results will appear here once it completes.
        </div>
      )}

      {!isRunning && isSkipRow && (
        <div
          className="mt-3 rounded-xl border border-amber-400/30 bg-amber-400/[0.05] px-4 py-3 text-[12px] text-amber-200"
          data-testid="screening-skip-banner"
        >
          <span className="font-semibold">Last run skipped: </span>
          {skipLabel}
        </div>
      )}

      {byTarget.length === 0 ? (
        <div className="mt-3 rounded-xl border border-white/[0.06] bg-white/[0.015] px-4 py-3 text-[12px] text-zinc-500">
          No screening overview yet. {isRunning ? "Run still in progress." : lastRun ? (isSkipRow ? "See last-run note above." : "All targets clean on the last run — and no overview captured. Re-run to refresh.") : "No screening run has been triggered."}
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {byTarget.map((t) => (
            <ScreeningTargetCard key={`${t.type}::${t.name}`} target={t} merchantId={merchantId} onRefresh={onRefresh} />
          ))}
        </div>
      )}
    </section>
  );
}


function ScreeningTargetCard({ target, merchantId, onRefresh }) {
  const [expanded, setExpanded] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const act = async (id, action) => {
    const note = action === "dismiss" ? window.prompt("Why is this a false positive?") : (window.prompt("Note (optional):") || "");
    if (action === "dismiss" && (!note || note.trim().length < 3)) return;
    setBusyId(id);
    try {
      await api.post(`/admin/merchants/${merchantId}/screening/findings/${id}/${action}`, { review_note: note });
      await onRefresh();
    } finally {
      setBusyId(null);
    }
  };
  const statusBadge = (s) => {
    const map = {
      pending: "bg-zinc-500/[0.10] text-zinc-300 border-zinc-400/20",
      confirmed_match: "bg-rose-500/[0.10] text-rose-300 border-rose-400/30",
      dismissed_match: "bg-zinc-500/[0.10] text-zinc-500 border-zinc-400/10",
      requires_more_info: "bg-amber-500/[0.10] text-amber-300 border-amber-400/30",
    };
    return map[s] || map.pending;
  };
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] overflow-hidden" data-testid={`screen-target-${target.type}-${target.name.replace(/\s+/g, "-")}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02]">
        <div className="text-left">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">{target.type}</div>
          <div className="text-[13px] text-zinc-200">{target.name}</div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-zinc-500">
          {target.overview && <span className="text-[10.5px] uppercase tracking-[0.14em] text-cyan-300/80">overview</span>}
          <span className="font-mono">{target.items.length} hit{target.items.length === 1 ? "" : "s"}</span>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>
      </button>
      {expanded && (
        <div className="border-t border-white/[0.05] px-4 py-3 space-y-3">
          {/* Brief 3.13 Phase 2 — general overview block. Shows above
              the structured findings so the underwriter reads the
              "who is this" context before scanning red flags. */}
          {target.overview && (
            <div
              data-testid={`screen-target-overview-${target.type}-${(target.name || "").replace(/\s+/g, "-")}`}
              className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-3"
            >
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-cyan-300/80 mb-1.5">Overview</div>
              <div className="text-[12.5px] text-zinc-200 leading-relaxed whitespace-pre-wrap">
                {target.overview}
              </div>
              {target.sources && target.sources.length > 0 && (
                <div className="mt-2.5 pt-2.5 border-t border-white/[0.04]">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Sources</div>
                  <ul className="space-y-0.5">
                    {target.sources.map((s, i) => (
                      <li key={`${s.url}-${i}`} className="text-[11px]">
                        <a href={s.url} target="_blank" rel="noopener noreferrer"
                           data-testid={`screen-target-source-${i}`}
                           className="text-sky-300/80 hover:text-sky-200 font-mono truncate inline-flex items-center gap-1.5">
                          <span className="inline-block rounded px-1.5 py-0.5 bg-white/[0.04] text-zinc-400 text-[9.5px] uppercase tracking-[0.12em]">{s.kind || "other"}</span>
                          <span className="truncate">{s.title || s.url}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {target.items.length === 0 && target.overview && (
            <div className="text-[11.5px] text-emerald-300/80">
              No adverse findings on this target.
            </div>
          )}
          {target.items.map((f) => (
            <div key={f.screening_finding_id} className={`rounded-lg border ${SEVERITY_STYLE[f.severity]?.bd || "border-white/10"} ${SEVERITY_STYLE[f.severity]?.bg || ""} px-3 py-2`} data-testid={`screen-finding-${f.screening_finding_id}`}>
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className={`text-[10.5px] uppercase tracking-[0.18em] ${SEVERITY_STYLE[f.severity]?.text || ""}`}>{f.severity}</span>
                <span className="text-[12px] text-zinc-300 capitalize">{(f.category || "").replace("_", " ")}</span>
                <span className="text-[10.5px] text-zinc-500">match: {f.match_status}</span>
                <span className="text-[10.5px] text-zinc-500 font-mono">{Number(f.match_confidence || 0).toFixed(2)}</span>
                <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded border ${statusBadge(f.reviewed_status)}`}>{(f.reviewed_status || "pending").replace("_", " ")}</span>
              </div>
              <div className="mt-1.5 text-[12px] text-zinc-400 leading-relaxed">{f.source_snippet}</div>
              {f.source_url && (
                <div className="mt-0.5 text-[11px] text-sky-400/80 font-mono truncate">{f.source_url}</div>
              )}
              {f.reasoning && (
                <div className="mt-1 text-[11px] text-zinc-500 italic">{f.reasoning}</div>
              )}
              {f.redacted && (
                <div className="mt-1 text-[10.5px] text-rose-400">⚠ output filter flagged potential cross-merchant data</div>
              )}
              {(f.reviewed_status || "pending") === "pending" && (
                <div className="mt-2 flex gap-2">
                  <button data-testid={`screen-confirm-${f.screening_finding_id}`} disabled={busyId === f.screening_finding_id} onClick={() => act(f.screening_finding_id, "confirm")} className="text-[11px] px-2.5 py-1 rounded-md bg-rose-500/[0.10] hover:bg-rose-500/[0.18] text-rose-300 border border-rose-400/30 disabled:opacity-40">Confirm match</button>
                  <button data-testid={`screen-dismiss-${f.screening_finding_id}`} disabled={busyId === f.screening_finding_id} onClick={() => act(f.screening_finding_id, "dismiss")} className="text-[11px] px-2.5 py-1 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-zinc-300 border border-white/[0.08] disabled:opacity-40">Dismiss false positive</button>
                  <button data-testid={`screen-more-info-${f.screening_finding_id}`} disabled={busyId === f.screening_finding_id} onClick={() => act(f.screening_finding_id, "more-info")} className="text-[11px] px-2.5 py-1 rounded-md bg-amber-500/[0.10] hover:bg-amber-500/[0.18] text-amber-300 border border-amber-400/30 disabled:opacity-40">Needs info</button>
                </div>
              )}
              {f.review_note && (
                <div className="mt-1 text-[10.5px] text-zinc-500">admin note: {f.review_note}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function ExtractionRow({ e }) {
  const [expanded, setExpanded] = useState(false);
  const fields = e.extracted_fields || {};
  const confs = e.field_confidences || {};
  const quotes = e.field_quotes || {};
  const status = e.extraction_status;
  const statusColor = {
    success: "text-emerald-300",
    low_confidence: "text-amber-300",
    doc_type_mismatch: "text-rose-300",
    failed_extraction: "text-rose-300",
    failed_schema: "text-rose-300",
    unsupported_file_type: "text-zinc-400",
    cap_exceeded: "text-zinc-400",
  }[status] || "text-zinc-400";
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] overflow-hidden" data-testid={`extraction-${e.extraction_id}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02]">
        <div className="flex items-center gap-3 text-left">
          <FileText className="h-4 w-4 text-zinc-500 flex-shrink-0" />
          <div>
            <div className="text-[13px] text-zinc-200 font-mono">{e.doc_key}</div>
            <div className="text-[10.5px] text-zinc-500">
              <span className={statusColor}>{status}</span>
              {e.doc_type_classified && <span> · classified: {e.doc_type_classified}</span>}
              {" · v" + (e.doc_version || 1)}
            </div>
          </div>
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-zinc-500" /> : <ChevronRight className="h-4 w-4 text-zinc-500" />}
      </button>
      {expanded && (
        <div className="border-t border-white/[0.05] px-4 py-3 space-y-2">
          {Object.keys(fields).length === 0 ? (
            <div className="text-[11px] text-zinc-500">No extracted fields.</div>
          ) : (
            <div className="space-y-1.5">
              {Object.entries(fields).map(([k, v]) => (
                <div key={k} className="grid grid-cols-[160px_1fr_auto] gap-3 items-start text-[12px]">
                  <div className="text-zinc-500 font-mono">{k}</div>
                  <div className="text-zinc-300 break-all">
                    {v == null ? <span className="text-zinc-600 italic">null</span> : typeof v === "object" ? <span className="font-mono">{JSON.stringify(v).slice(0, 200)}</span> : String(v)}
                    {quotes[k]?.quote && (
                      <div className="text-[10.5px] text-zinc-500 italic mt-0.5">&ldquo;{quotes[k].quote}&rdquo; <span className="text-zinc-600">(p. {quotes[k].page ?? "?"})</span></div>
                    )}
                  </div>
                  <ConfidenceBadge value={confs[k]} />
                </div>
              ))}
            </div>
          )}
          {(e.warnings || []).length > 0 && (
            <div className="mt-2 text-[11px] text-amber-300">⚠ {(e.warnings || []).join(" · ")}</div>
          )}
        </div>
      )}
    </div>
  );
}


export default function AdminCrossCheck() {
  const { merchantId } = useParams();
  const [data, setData] = useState(null);
  const [screening, setScreening] = useState(null);
  const [loading, setLoading] = useState(true);
  // Brief 3.5 PR 1 reliability — split the previous single `err` into two
  // independent error surfaces so a screening failure can't hide cross-check
  // data and vice-versa. The header `err` field stays for compatibility but
  // is only set when BOTH bundles fail (otherwise the panel still renders).
  const [err, setErr] = useState("");
  const [crossCheckErr, setCrossCheckErr] = useState("");
  const [screeningErr, setScreeningErr] = useState("");
  const [filterSev, setFilterSev] = useState("all");
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [rerunning, setRerunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    setCrossCheckErr("");
    setScreeningErr("");
    try {
      // Brief 3.5 PR 1 — Promise.allSettled so each endpoint's failure is
      // surfaced in its own banner. Previously Promise.all turned ANY failure
      // into a single "Failed to load" that hid all data, even the half that
      // came back fine.
      const [ccRes, scRes] = await Promise.allSettled([
        api.get(`/admin/merchants/${merchantId}/cross-check`),
        api.get(`/admin/merchants/${merchantId}/screening`),
      ]);
      if (ccRes.status === "fulfilled") {
        setData(ccRes.value.data);
      } else {
        const detail = ccRes.reason?.response?.data?.detail;
        setCrossCheckErr(typeof detail === "string" ? detail :
          "Cross-check could not load. Click Re-run all or try again in a moment.");
        setData(null);
      }
      if (scRes.status === "fulfilled") {
        setScreening(scRes.value.data);
      } else {
        const detail = scRes.reason?.response?.data?.detail;
        setScreeningErr(typeof detail === "string" ? detail :
          "Screening data could not load. Cross-check above is unaffected.");
        setScreening(null);
      }
      // Header-level error ONLY when both halves failed — keep the historical
      // banner behaviour for tests that look for `cc-error`.
      if (ccRes.status === "rejected" && scRes.status === "rejected") {
        setErr("Failed to load.");
      }
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => { load(); }, [load]);

  const filteredFindings = useMemo(() => {
    const arr = data?.open_findings || [];
    if (filterSev === "all") return arr;
    return arr.filter((f) => f.severity === filterSev);
  }, [data, filterSev]);

  const counts = data?.counts || {};
  const totalOpen = SEVERITY_ORDER.reduce((acc, s) => acc + (counts[s] || 0), 0);

  const onRerun = async (opts = {}) => {
    if (rerunning) return;
    const force = Boolean(opts.force);
    if (force) {
      // Explicit AM confirmation — force bypasses the 60-second
      // cooldown AND the per-merchant daily LLM cap AND the cache.
      // Every doc is re-extracted with a fresh LLM call so this
      // burns real tokens.
      const ok = window.confirm(
        "Force re-run will ignore the 60s cooldown AND the per-merchant " +
        "daily LLM cap, and bypass the extraction cache so every doc is " +
        "re-extracted from scratch. This burns real tokens — only do this " +
        "after a prompt/schema change or when you suspect cached data is " +
        "wrong.\n\nContinue?"
      );
      if (!ok) return;
    }
    setRerunning(true);
    try {
      const url = `/admin/merchants/${merchantId}/cross-check/rerun${force ? "?force=true" : ""}`;
      const res = await api.post(url);
      const sStatus = res?.data?.screening_status;
      if (sStatus && sStatus !== "ok") {
        alert(`Re-run complete. Extractions refreshed. Screening: ${sStatus.replace(/_/g, " ")}.`);
      }
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail) alert(detail);
    } finally {
      setRerunning(false);
    }
  };

  const onResolveConfirm = async (note) => {
    if (!resolveTarget) return;
    setResolving(true);
    try {
      await api.post(`/admin/merchants/${merchantId}/cross-check/findings/${resolveTarget.finding_id}/resolve`, { resolution_note: note });
      setResolveTarget(null);
      await load();
    } finally {
      setResolving(false);
    }
  };

  const onDownloadPdf = () => {
    const url = api.defaults.baseURL + `/admin/merchants/${merchantId}/cross-check/pdf`;
    const link = document.createElement("a");
    link.href = url;
    // Browser will pick up the auth cookie. If we use bearer-only mode, need a fetch+blob fallback.
    api.get(`/admin/merchants/${merchantId}/cross-check/pdf`, { responseType: "blob" })
       .then((res) => {
         const blobUrl = URL.createObjectURL(res.data);
         link.href = blobUrl;
         link.download = `neptune-crosscheck-${merchantId}.pdf`;
         document.body.appendChild(link);
         link.click();
         link.remove();
         URL.revokeObjectURL(blobUrl);
       })
       .catch((e) => alert(e?.response?.data?.detail || "PDF generation failed."));
  };

  return (
    <div className="max-w-5xl">
      <Link to={`/admin/merchants/${merchantId}`} data-testid="cc-back" className="inline-flex items-center gap-1 text-[12px] text-zinc-400 hover:text-[#00E5FF]">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to merchant
      </Link>
      <div className="mt-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
        <Shield className="h-3 w-3 text-[#00E5FF]" /> Cross-check
      </div>
      <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text">
        Underwriting findings
      </h1>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-[12px] text-zinc-500">
        <span data-testid="cc-merchant-id" className="font-mono">{merchantId}</span>
        {data?.last_extraction_at && <span>· last extraction {(data.last_extraction_at || "").slice(0, 19).replace("T", " ")}</span>}
        {data?.last_comparison_at && <span>· last comparison {(data.last_comparison_at || "").slice(0, 19).replace("T", " ")}</span>}
      </div>

      {err && <div className="mt-4 rounded-lg border border-rose-400/30 bg-rose-500/[0.05] px-3 py-2 text-[12.5px] text-rose-200" data-testid="cc-error">{err}</div>}
      {/* Brief 3.5 PR 1 — independent per-bundle error banners. Each surfaces
          only when its own endpoint failed. Stays visible until next load. */}
      {crossCheckErr && !err && (
        <div
          className="mt-4 rounded-lg border border-amber-400/30 bg-amber-500/[0.05] px-3 py-2 text-[12.5px] text-amber-200"
          data-testid="cc-error-cross-check"
        >
          {crossCheckErr}
        </div>
      )}
      {screeningErr && !err && (
        <div
          className="mt-2 rounded-lg border border-amber-400/30 bg-amber-500/[0.05] px-3 py-2 text-[12.5px] text-amber-200"
          data-testid="cc-error-screening"
        >
          {screeningErr}
        </div>
      )}

      <StateBanner state={data?.state} />

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button data-testid="cc-rerun" disabled={rerunning} onClick={() => onRerun()} className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/30 disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${rerunning ? "animate-spin" : ""}`} />
          {rerunning ? "Re-running…" : "Re-run all"}
        </button>
        <button
          data-testid="cc-rerun-force"
          disabled={rerunning}
          onClick={() => onRerun({ force: true })}
          title="Bypass the 60s cooldown, daily LLM cap, and extraction cache. Burns tokens — use after a prompt/schema change."
          className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg bg-amber-400/10 hover:bg-amber-400/20 text-amber-200 border border-amber-400/30 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${rerunning ? "animate-spin" : ""}`} />
          Force re-run (bypass cache & cap)
        </button>
        <button data-testid="cc-download-pdf" onClick={onDownloadPdf} className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-zinc-200 border border-white/[0.10]">
          <Download className="h-3.5 w-3.5" /> Download PDF
        </button>
      </div>

      <section className="mt-7" data-testid="cc-findings-section">
        <div className="flex items-center justify-between">
          <h2 className="text-[14px] font-semibold text-zinc-200 inline-flex items-center gap-2">
            Findings <span className="text-zinc-500 font-normal">({totalOpen} open)</span>
          </h2>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {[["all", totalOpen]].concat(SEVERITY_ORDER.map((s) => [s, counts[s] || 0])).map(([k, n]) => (
            <button
              key={k}
              data-testid={`cc-filter-${k}`}
              onClick={() => setFilterSev(k)}
              className={`text-[11.5px] px-2.5 py-1 rounded-md border transition-colors ${
                filterSev === k
                  ? "bg-[#00E5FF]/10 border-[#00E5FF]/30 text-[#00E5FF]"
                  : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:bg-white/[0.05]"
              }`}
            >
              {k === "all" ? "All" : k.charAt(0).toUpperCase() + k.slice(1)} <span className="opacity-70 font-mono">{n}</span>
            </button>
          ))}
        </div>
        <div className="mt-3 space-y-2.5" data-testid="cc-findings-list">
          {filteredFindings.length === 0 ? (
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] px-4 py-6 text-center text-[12px] text-zinc-500">
              {totalOpen === 0 ? "No open findings." : "No findings at this severity."}
            </div>
          ) : (
            filteredFindings.map((f) => (
              <FindingCard key={f.finding_id} f={f} onResolve={setResolveTarget} />
            ))
          )}
        </div>
      </section>

      <ScreeningSection data={screening} merchantId={merchantId} onRefresh={load} />

      <section className="mt-8" data-testid="cc-screening-summary-section">
        <h2 className="text-[14px] font-semibold text-zinc-200">Screening summary</h2>
        <p className="text-[11.5px] text-zinc-500 mt-1 mb-4">
          What was checked, what was found, what was filtered — audit-grade snapshot of the last run.
        </p>
        <ScreeningSummaryPanel merchantId={merchantId} />
      </section>

      <section className="mt-8" data-testid="cc-extractions-section">
        <h2 className="text-[14px] font-semibold text-zinc-200">Document inventory</h2>
        <div className="mt-3 space-y-2">
          {(data?.extractions || []).length === 0 ? (
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] px-4 py-6 text-center text-[12px] text-zinc-500">
              No extractions yet.
            </div>
          ) : (
            (data?.extractions || []).map((e) => <ExtractionRow key={e.extraction_id} e={e} />)
          )}
        </div>
      </section>

      <ResolveModal finding={resolveTarget} onClose={() => setResolveTarget(null)} onConfirm={onResolveConfirm} busy={resolving} />
    </div>
  );
}
